import type { ReactNode } from 'react';

import _ from 'lodash';
import {
  BadgeDollarSignIcon,
  CircleCheckIcon,
  CoinsIcon,
  Truck,
} from 'lucide-react';

import type { QuoteCardType } from 'components/QuoteCard';
import { SonarQuoteMetadata } from 'lib/api/getQuickQuote';
import { TransportType } from 'types/QuoteRequest';
import type { Maybe } from 'types/UtilityTypes';
import { titleCase } from 'utils/formatStrings';
import { getDrumkitContainer } from 'utils/getDrumkitContainer';
import { cn } from 'utils/shadcn';

import {
  Tooltip,
  TooltipContent,
  TooltipPortal,
  TooltipProvider,
  TooltipTrigger,
} from './Tooltip';
import { Flex } from './layout';
import { Typography } from './typography';

type QuoteCardSonarProps = {
  carrier: QuoteCardType;
  isSelected: boolean;
  onClick: () => void;
};

export function QuoteCardSonar({
  carrier,
  isSelected,
  onClick,
}: QuoteCardSonarProps) {
  return (
    <TooltipProvider>
      <Flex
        key={carrier.type}
        direction='col'
        className={cn(
          'relative w-full border-2 border-brand-400 rounded-[4px] p-2 transition-transform',
          isSelected
            ? 'bg-brand-50'
            : 'cursor-pointer hover:scale-[1.01] bg-neutral-50'
        )}
        onClick={onClick}
      >
        <Flex justify='between' align='center' className='w-full mb-2'>
          <Flex direction='col'>
            <Flex align='center' gap='xs'>
              <Typography variant='body-sm' weight='semibold'>
                {carrier.title}
              </Typography>
            </Flex>
          </Flex>
          {carrier.icon}
        </Flex>

        <SonarDetailSection
          metadata={carrier.sonarMetadata ?? {}}
          carrier={carrier}
        />

        {isSelected && (
          <CircleCheckIcon
            data-testid='quote-card-selected-check'
            className='absolute -top-3 -right-2.5 fill-neutral-50 stroke-success-500! w-6 h-6'
            aria-hidden
          />
        )}
      </Flex>
    </TooltipProvider>
  );
}

function SonarDetailSection({
  metadata,
  carrier,
}: {
  metadata: Partial<SonarQuoteMetadata>;
  carrier: QuoteCardType;
}) {
  const popoverRootContainer = getDrumkitContainer();

  const roundedCost = _.round(carrier.cost);
  const roundedCostPerMile =
    carrier.costPerMile != null ? _.round(carrier.costPerMile, 2) : null;
  const roundedPriceRange = carrier.priceRange
    ? {
        lowEstimate: _.round(carrier.priceRange.lowEstimate),
        highEstimate: _.round(carrier.priceRange.highEstimate),
      }
    : null;
  const roundedPriceRangePerMile = carrier.priceRangePerMile
    ? {
        lowEstimate: _.round(carrier.priceRangePerMile.lowEstimate, 2),
        highEstimate: _.round(carrier.priceRangePerMile.highEstimate, 2),
      }
    : null;

  const {
    confidenceScore,
    laneScore,
    capacityIndicator,
    current_capacity_condition: currentCapacityCondition,
    directional_capacity_change: directionalCapacityChange,
    coverage_guidance: coverageGuidance,
  } = metadata;

  const hasCoverageGuideCapacity =
    Boolean(currentCapacityCondition) ||
    Boolean(directionalCapacityChange) ||
    Boolean(coverageGuidance);

  const sonarSkipsCapacity =
    carrier.actualTransportType === TransportType.FLATBED;

  const currentCapacityColor = sonarCapacityColorCoding(
    currentCapacityCondition ?? ''
  );

  return (
    <Flex direction='col' className='w-full gap-0'>
      <Flex justify='between' align='center' className='w-full mt-2'>
        <Flex align='center' className='gap-x-1.5'>
          <Truck className='h-4 w-4' />
          <Typography variant='body-sm'>Transport Type</Typography>
        </Flex>
        {carrier.inputtedTransportType !== carrier.actualTransportType ? (
          <Flex align='baseline' gap='sm'>
            <Tooltip delayDuration={10}>
              <TooltipTrigger className='border-b border-dashed border-neutral-900'>
                <Typography variant='body-sm'>
                  {titleCase(carrier.actualTransportType)}
                </Typography>
              </TooltipTrigger>
              <TooltipPortal container={popoverRootContainer}>
                <TooltipContent className='mr-1 max-w-60 whitespace-pre-wrap'>
                  <Typography variant='body-xs'>
                    {`${carrier.quotingIntegrationName ?? 'Quoting integration'} `}
                    does not support {titleCase(carrier.inputtedTransportType)}{' '}
                    quotes, showing equivalent{' '}
                    {titleCase(carrier.actualTransportType)} quote.
                  </Typography>
                </TooltipContent>
              </TooltipPortal>
            </Tooltip>
          </Flex>
        ) : (
          <Typography variant='body-sm'>
            {titleCase(carrier.actualTransportType)}
          </Typography>
        )}
      </Flex>

      <Flex justify='between' align='center' className='w-full'>
        <Flex align='center' gap='sm'>
          <BadgeDollarSignIcon className='h-4 w-4' />
          <Typography variant='body-sm'>Buy Rate</Typography>
        </Flex>
        <Flex align='baseline' className='gap-x-2 flex-wrap justify-end'>
          <Typography variant='body-xs'>{`$${roundedCost.toLocaleString()}`}</Typography>
          {roundedCostPerMile != null && (
            <Typography variant='body-xs'>{`($${roundedCostPerMile.toLocaleString()}/mi)`}</Typography>
          )}
        </Flex>
      </Flex>

      {roundedPriceRange != null && (
        <Flex justify='between' align='center' className='w-full'>
          <Flex align='center' gap='sm'>
            <CoinsIcon className='h-4 w-4' />
            <Typography variant='body-sm'>Price Range</Typography>
          </Flex>
          <Flex align='center' className='gap-x-2'>
            {roundedPriceRangePerMile ? (
              <Tooltip delayDuration={10}>
                <TooltipTrigger className='border-b border-dashed border-neutral-900'>
                  <Typography variant='body-xs'>
                    {`$${roundedPriceRange.lowEstimate.toLocaleString()} - $${roundedPriceRange.highEstimate.toLocaleString()}`}
                  </Typography>
                </TooltipTrigger>
                <TooltipPortal container={popoverRootContainer}>
                  <TooltipContent className='mr-2 max-w-60 whitespace-pre-wrap'>
                    <div className='pb-3'>
                      <Typography variant='body-xs'>
                        Price range per mile
                      </Typography>
                      <Typography variant='body-xs'>
                        {`$${roundedPriceRangePerMile.lowEstimate.toLocaleString()} - $${roundedPriceRangePerMile.highEstimate.toLocaleString()}/mi.`}
                      </Typography>
                    </div>
                    <Typography variant='body-sm' className='border-t pt-3'>
                      All prices include fuel.
                    </Typography>
                  </TooltipContent>
                </TooltipPortal>
              </Tooltip>
            ) : (
              <Typography variant='body-xs'>
                {`$${roundedPriceRange.lowEstimate.toLocaleString()} - $${roundedPriceRange.highEstimate.toLocaleString()}`}
              </Typography>
            )}
          </Flex>
        </Flex>
      )}

      <div className='w-full border-t border-neutral-200 my-3' />

      {confidenceScore != null && (
        <Flex justify='between' align='center' className='w-full gap-2'>
          <Typography className='shrink-0 text-[13px]'>
            TRAC Confidence
          </Typography>
          <Typography variant='body-xs' className='text-right'>
            {confidenceScore} / 5
          </Typography>
        </Flex>
      )}

      {laneScore != null && (
        <Flex justify='between' align='center' className='w-full mt-1 gap-2'>
          <Typography className='shrink-0 text-[13px]'>Lane Score</Typography>
          <Typography variant='body-xs' className='text-right'>
            {laneScore} / 100
          </Typography>
        </Flex>
      )}

      {renderSonarCapacitySection({
        capacityIndicator,
        hasCoverageGuideCapacity,
        currentCapacityCondition,
        directionalCapacityChange,
        coverageGuidance,
        currentCapacityColor,
        laneScore,
        sonarSkipsCapacity,
      })}
    </Flex>
  );
}

function renderCoverageGuideCapacityRows({
  currentCapacityCondition,
  directionalCapacityChange,
  coverageGuidance,
  currentCapacityColor,
}: {
  currentCapacityCondition?: Maybe<string>;
  directionalCapacityChange?: Maybe<string>;
  coverageGuidance?: Maybe<string>;
  currentCapacityColor: string;
}): ReactNode {
  return (
    <>
      {currentCapacityCondition && (
        <Flex justify='between' align='center' className='w-full mt-1 gap-2'>
          <Typography className='shrink-0 text-[13px]'>
            Current Capacity
          </Typography>
          <Typography
            variant='body-xs'
            weight='semibold'
            className={cn('text-right', currentCapacityColor)}
          >
            {currentCapacityCondition}
          </Typography>
        </Flex>
      )}
      {directionalCapacityChange && (
        <Flex justify='between' align='center' className='w-full mt-1 gap-2'>
          <Typography className='shrink-0 text-[13px]'>
            Directional Change
          </Typography>
          <Typography
            variant='body-xs'
            weight='semibold'
            className={cn(
              'text-right',
              sonarCapacityColorCoding(directionalCapacityChange)
            )}
          >
            {directionalCapacityChange}
          </Typography>
        </Flex>
      )}
      {coverageGuidance && (
        <Flex justify='between' align='center' className='w-full mt-1 gap-2'>
          <Typography className='shrink-0 text-[13px]'>
            Coverage Guidance
          </Typography>
          <Typography variant='body-xs' className='text-right'>
            {coverageGuidance}
          </Typography>
        </Flex>
      )}
    </>
  );
}

function renderSonarCapacitySection({
  capacityIndicator,
  hasCoverageGuideCapacity,
  currentCapacityCondition,
  directionalCapacityChange,
  coverageGuidance,
  currentCapacityColor,
  laneScore,
  sonarSkipsCapacity,
}: {
  capacityIndicator?: Maybe<string>;
  hasCoverageGuideCapacity: boolean;
  currentCapacityCondition?: Maybe<string>;
  directionalCapacityChange?: Maybe<string>;
  coverageGuidance?: Maybe<string>;
  currentCapacityColor: string;
  laneScore?: number;
  sonarSkipsCapacity: boolean;
}): ReactNode {
  if (capacityIndicator) {
    return (
      <Flex justify='between' align='center' className='w-full mt-1 gap-2'>
        <Typography className='shrink-0 text-[13px]'>Capacity</Typography>
        <Typography variant='body-xs' weight='semibold' className='text-right'>
          {capacityIndicator}
        </Typography>
      </Flex>
    );
  }

  if (hasCoverageGuideCapacity) {
    return renderCoverageGuideCapacityRows({
      currentCapacityCondition,
      directionalCapacityChange,
      coverageGuidance,
      currentCapacityColor,
    });
  }

  if (laneScore != null) {
    return null;
  }

  if (sonarSkipsCapacity) {
    return (
      <Typography variant='body-xs' className='text-neutral-600 mt-1'>
        Lane capacity not available for this equipment.
      </Typography>
    );
  }

  return (
    <Typography variant='body-xs' className='text-neutral-600 mt-1'>
      Lane capacity data unavailable.
    </Typography>
  );
}

function sonarCapacityColorCoding(value: string): string {
  const t = value.trim();
  if (!t) {
    return 'text-neutral-900';
  }

  const isTightRegex =
    /\b(tighten(ing|ed|s|e|er|ing)?|tightening|tightened|tightens|tight)\b/i;
  const isBalancedRegex =
    /\b(balanc\w*|moderate|stable|minimal|average|neutral|fair)\b/i;
  const isLooseRegex =
    /\b(loose|looses|loosens?|loosening|loosened|looser|loosed|loosing)\b/i;

  if (isLooseRegex.test(t) && !isTightRegex.test(t)) {
    return 'text-success-600';
  }
  if (isBalancedRegex.test(t)) {
    return 'text-brand-400';
  }
  if (isTightRegex.test(t)) {
    return 'text-error-500';
  }
  return 'text-neutral-900';
}
